import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import RegionFilter from '../region-filter';
import AfilliationFilter from '../affiliation-unit';
import ProspectFilter from '../prospect-filter';
import styles from './filterBy.module.css';
import LifetimeGivingFilter from '../lifetime-giving';
import TotalConnectionsFilter from '../total-connections';
import DegreesOfSeparationFilter from '../degrees-separation';
import ConstituencyTypeFilter from '../constituency-type';
// import TagsFilter from '../tags';
import '../styles.css';
import { clearNodeFilters, setAffiliationClusterMode } from 'store/filters/actions';
import NodeSizing from '../node-sizing';
import { setView } from 'store/graph/actions';
import ReactTooltip from 'react-tooltip';
import { selectHiddenNodes } from 'store/filters/selectors';
import HiddenNodesFilter from '../hidden-nodes';
import ShowDeceasedFilter from '../show-deceased';
import AgeFilter from '../age-filter';
import FundPurposeFilter from '../fund-purpose-filter';
import { selectSearchMode } from 'store/core/selectors';
import { executeFilterSearch } from 'store/core/actions';
import { isFilterSearchEnabled } from 'utils/feature-flags';
import {
  selectRegionFilter,
  selectAffiliationFilter,
  selectTotalLifetimeGivingMin,
  selectTotalLifetimeGivingMax,
  selectProspectStatusForFilter,
  selectConstituencyFilter,
  selectAgeFilter,
} from 'store/filters/selectors';

const FilterBy = () => {
  const dispatch = useDispatch();
  const hiddenNodes = useSelector(selectHiddenNodes);
  const searchMode = useSelector(selectSearchMode);

  // Filter state selectors for building backend filter payload
  const regionFilter = useSelector(selectRegionFilter);
  const affiliationFilter = useSelector(selectAffiliationFilter);
  const lifetimeGivingMin = useSelector(selectTotalLifetimeGivingMin);
  const lifetimeGivingMax = useSelector(selectTotalLifetimeGivingMax);
  const prospectStatusFilter = useSelector(selectProspectStatusForFilter);
  const constituencyFilter = useSelector(selectConstituencyFilter);
  const ageFilter = useSelector(selectAgeFilter);

  const isFilterMode = searchMode === 'Filters' && isFilterSearchEnabled();

  // Build the active filters array for the backend
  const activeFilters = useMemo(() => {
    const filters = [];
    if (regionFilter && regionFilter.length > 0) {
      filters.push({ Type: 'region', FilterValue: Array.isArray(regionFilter) ? regionFilter : [regionFilter] });
    }
    if (affiliationFilter && Object.keys(affiliationFilter).length > 0) {
      const affValues = Object.keys(affiliationFilter);
      filters.push({ Type: 'affiliation', FilterValue: affValues });
    }
    if (lifetimeGivingMin !== undefined || lifetimeGivingMax !== undefined) {
      filters.push({
        Type: 'lifetime_giving',
        Filters: [
          { FilterValue: lifetimeGivingMin || 0 },
          { FilterValue: lifetimeGivingMax || 999999999 },
        ],
      });
    }
    if (prospectStatusFilter && prospectStatusFilter.length > 0) {
      filters.push({ Type: 'prospect', FilterValue: prospectStatusFilter });
    }
    if (constituencyFilter && Object.keys(constituencyFilter).length > 0) {
      const constValues = Object.keys(constituencyFilter);
      filters.push({ Type: 'constituency', FilterValue: constValues });
    }
    if (ageFilter && (ageFilter.start !== undefined || ageFilter.end !== undefined)) {
      filters.push({
        Type: 'age',
        Filters: [
          { FilterValue: ageFilter.start || 0 },
          { FilterValue: ageFilter.end || 120 },
        ],
      });
    }
    return filters;
  }, [regionFilter, affiliationFilter, lifetimeGivingMin, lifetimeGivingMax, prospectStatusFilter, constituencyFilter, ageFilter]);

  const filterCount = activeFilters.length;
  const isReady = filterCount >= 2;

  const hiddenNodeIds = Object.keys(hiddenNodes);
  const clear = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    dispatch(clearNodeFilters());
  };
  const view = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    dispatch(setView());
  };

  const handleExecuteSearch = () => {
    if (isReady) {
      dispatch(executeFilterSearch(activeFilters));
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header_wrapper}>
        <p className={styles.search_header} onClick={() => view()}>
          Filter By
        </p>
        <div
          className={styles.header_type}
          onClick={clear}
          data-for="clear-map-node-filters"
          data-tip
        >
          Clear All
        </div>
        <ReactTooltip id="clear-map-node-filters" place="right" effect="solid">
          <div style={{ maxWidth: '110px' }}>
            This action will reset all Filters
          </div>
        </ReactTooltip>
      </div>
      {isFilterMode && (
        <div
          className={styles.filter_status_bar}
          style={{
            background: isReady ? 'rgba(56, 178, 217, 0.15)' : 'rgba(224, 168, 0, 0.15)',
            borderLeft: isReady ? '3px solid #38b2d9' : '3px solid #e0a800',
            padding: '8px 12px',
            fontSize: '12px',
            color: isReady ? '#38b2d9' : '#e0a800',
            fontWeight: 600,
          }}
        >
          {isReady
            ? `✓ ${filterCount} filters — ready to search`
            : `⚠ ${filterCount} filter(s) — need 2 minimum`}
        </div>
      )}
      {isFilterMode && (
        <div style={{ padding: '8px 16px 4px' }}>
          <button
            onClick={handleExecuteSearch}
            disabled={!isReady}
            style={{
              width: '100%',
              height: '36px',
              background: isReady ? '#38b2d9' : '#555',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 700,
              cursor: isReady ? 'pointer' : 'not-allowed',
              opacity: isReady ? 1 : 0.5,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Execute Search ({filterCount} filters)
          </button>
        </div>
      )}
      <div className={styles.header_row}>
        <p className={styles.search_header}>Map Filters</p>
      </div>
      <AfilliationFilter />
      <ProspectFilter />
      <FundPurposeFilter />
      <LifetimeGivingFilter />
      <ConstituencyTypeFilter />
      <TotalConnectionsFilter />
      <RegionFilter />
      <NodeSizing />
      <ShowDeceasedFilter />
      <AgeFilter />
      <div className={styles.header_row}>
        <p className={styles.search_header}>Node Filters</p>
      </div>
      <DegreesOfSeparationFilter />
      {hiddenNodeIds.length > 0 && (
        <HiddenNodesFilter nodes={hiddenNodes} hiddenNodeIds={hiddenNodeIds} />
      )}
    </div>
  );
};

export default FilterBy;
